import React, { useEffect, useRef } from 'react';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { bonuses, offer, price } from '../data/content';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton } from './CtaButton';
import { ImageSlot } from './ImageSlot';
import { SectionHeading } from './SectionHeading';
type OfferSectionProps = {
  offerMockupUrl: string;
};
export function OfferSection({
  offerMockupUrl
}: OfferSectionProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const observer = new IntersectionObserver(entries => {
      if (!fired && entries.some(e => e.isIntersecting)) {
        fired = true;
        trackPixel('ViewContent');
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} id="offer" aria-labelledby="offer-title" className="scroll-mt-0 bg-teal py-12 md:py-20" data-id="element-133" data-mp="12n8mtwmk42n9">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-134" data-mp="xwjt0anemtky">
        <SectionHeading id="offer-title" tone="dark" title={offer.title} data-id="element-135" data-mp="xupaf2zlvdk1" />

        <div className="mx-auto mt-8 max-w-[820px] overflow-hidden rounded-2xl bg-white md:mt-12" data-id="element-136" data-mp="sy8h7udju903">
          <div className="aspect-[16/10] bg-teal-tint p-3 sm:aspect-[16/8] sm:p-5" data-id="element-137" data-mp="oxood300drjr">
            <ImageSlot url={offerMockupUrl} alt="Everything included in Preach Ready™" slotName="offerMockupUrl" fit="contain" data-id="element-138" data-mp="1ll32uum3ref5" />
          </div>

          <div className="p-4 pb-6 sm:p-8 md:p-10" data-id="element-139" data-mp="1glgky7vbxa00">
            <h3 className="text-center font-display text-[17px] font-extrabold uppercase leading-snug tracking-tight text-teal sm:text-[20px] md:text-[24px]" data-id="element-140" data-mp="1ckd8lvchpw3j">
              {offer.cardTitle}
            </h3>

            {/* Price block — placed high so it's seen immediately on mobile */}
            <div className="mt-6 rounded-xl bg-cream px-4 py-6 text-center" data-id="element-141" data-mp="3sbsp5ow6a16e">
              <p className="font-display text-[4.5rem] font-extrabold leading-none tracking-tight text-orange md:text-[5.5rem]" data-id="element-142" data-mp="3tthogr91uy69">
                {price}
              </p>
              <p className="mt-2 text-[12px] font-bold tracking-[0.1em] text-teal" data-id="element-143" data-mp="2lc57ok5kjej">{offer.priceNote}</p>
            </div>

            <div className="mt-8 grid gap-7 md:grid-cols-2 md:gap-10" data-id="element-144" data-mp="3oumxh20xi3b2">
              <div data-id="element-145" data-mp="3owr8s515q4ld">
                <h4 className="font-display text-[13px] font-extrabold tracking-[0.1em] text-orange-ink" data-id="element-146" data-mp="3b3rug6d42gfe">{offer.includedTitle}</h4>
                <ul className="mt-4 space-y-3" data-id="element-147" data-mp="3fv9tgovkq6xq">
                  {offer.included.map(item => <li key={item} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-148" data-mp="316dd84jd5nm1">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal text-white" data-id="element-149" data-mp="365gkzwqqa7xp">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-150" data-mp="2s5fi6bqwkqfl" />
                      </span>
                      {item}
                    </li>)}
                </ul>
              </div>
              <div className="border-t border-cream-line pt-7 md:border-l md:border-t-0 md:pl-10 md:pt-0" data-id="element-151" data-mp="28iu7nqpjdlk5">
                <h4 className="font-display text-[13px] font-extrabold tracking-[0.1em] text-orange-ink" data-id="element-152" data-mp="2midf0haiwsve">{offer.bonusesTitle}</h4>
                <ul className="mt-4 space-y-3" data-id="element-153" data-mp="2ifyrvkztn6yb">
                  {bonuses.items.map(b => <li key={b.label} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-154" data-mp="2wfrhte3ixj58">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold text-teal-dark" data-id="element-155" data-mp="2rgonoop30xc1">
                        <GiftIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-156" data-mp="365t90er6bhod" />
                      </span>
                      <span data-id="element-157" data-mp="3f7ai3d65yj3k">{toTitle(b.title)}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="mx-auto mt-9 max-w-[440px]" data-id="element-158" data-mp="3a9ipegxp2syb">
              <CtaButton action="checkout" microcopy={offer.microcopy} align="center" data-id="element-159" data-mp="18ja4hltolujq" />
            </div>
            <ul className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[13px] text-ink/65" data-id="element-160" data-mp="1dhlfw0zmkd42">
              {offer.delivery.map((d, i) => <li key={d} className="flex items-center gap-3" data-id="element-161" data-mp="1iay10153i1s2" data-mpi={i}>
                  {i > 0 && <span aria-hidden="true" className="text-orange" data-id="element-162" data-mp="1mcj9laaljtwg">
                      ·
                    </span>}
                  {d}
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function toTitle(upper: string) {
  const small = new Set(['a', 'an', 'and', 'for', 'of', 'the', 'to', 'in', 'on']);
  return upper.toLowerCase().split(' ').map((w, i) => {
    if (w === '—') return w;
    if (i > 0 && small.has(w)) return w;
    return w.split('-').map(p => /^\d/.test(p) || p === 'b' ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)).join('-');
  }).join(' ');
}